import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { courses } from '@/mocks/courses';

const courseNameMap: Record<string, string> = {
  'chatgpt-mastery': 'ChatGPT & Prompt Engineering Mastery',
  'ml-foundations': 'Machine Learning Foundations',
  'ai-agents': 'Building AI Agents & Automation',
  'ai-for-devs': 'AI for Developers: APIs to Production',
  'bundle': 'Complete AI Mastery Bundle',
};

const coursePriceMap: Record<string, string> = {
  'chatgpt-mastery': '$49',
  'ml-foundations': '$89',
  'ai-agents': '$119',
  'ai-for-devs': '$99',
  'bundle': '$249',
};

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const selectedCourse = searchParams.get('course') || '';
  const courseTitle = courseNameMap[selectedCourse] || 'Selected Course';
  const coursePrice = coursePriceMap[selectedCourse] || '—';

  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMessage('Thank you! Your order has been received. We will contact you shortly.');
      form.reset();
      return;
    }

    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d96i03cut53jbls7ko9g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      const serverMsg = (parsed?.meta as Record<string, string> | undefined)?.message
        || (parsed?.meta as Record<string, string> | undefined)?.detail
        || (parsed as Record<string, string> | null)?.message
        || responseText;

      if (response.ok && (parsed as Record<string, string> | null)?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('Thank you! Your order has been received. We will contact you shortly with payment details.');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage(typeof serverMsg === 'string' ? serverMsg : 'Something went wrong. Please try again or contact us directly.');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold text-primary-500 uppercase tracking-widest mb-3">
              Secure Checkout
            </p>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
              Complete Your Enrollment
            </h1>
            <p className="mt-3 text-base text-foreground-600">
              Fill in your details below to enroll in <strong>{courseTitle}</strong>.
            </p>
          </div>

          <div className="bg-background-100 rounded-lg p-6 md:p-8 mb-8 border border-background-200">
            <div className="flex items-center justify-between pb-4 border-b border-background-200 mb-4">
              <div>
                <p className="text-sm font-medium text-foreground-950">{courseTitle}</p>
                <p className="text-xs text-foreground-500">Lifetime access · Certificate included</p>
              </div>
              <span className="text-xl font-heading font-bold text-foreground-950">{coursePrice}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-foreground-950">Total</span>
              <span className="text-xl font-heading font-bold text-primary-500">{coursePrice}</span>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8 space-y-5"
          >
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-foreground-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                required
                className="w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
                placeholder="Your full name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                className="w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="course_selected" className="block text-sm font-medium text-foreground-700 mb-1">
                Selected Course *
              </label>
              <input
                type="text"
                id="course_selected"
                name="course_selected"
                value={courseTitle}
                readOnly
                className="w-full h-10 px-3 rounded-md border border-background-200 bg-background-100 text-sm text-foreground-950 outline-none"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                Additional Notes
              </label>
              <textarea
                id="message"
                name="message"
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-200 resize-none"
                placeholder="Any special requests or questions?"
              />
            </div>

            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="absolute opacity-0 pointer-events-none"
            />

            {formStatus === 'success' && (
              <div className="bg-accent-100 border border-accent-200 rounded-md px-4 py-3 text-sm text-accent-800">
                <i className="ri-check-line w-4 h-4 inline-flex items-center justify-center mr-1" />
                {formMessage}
              </div>
            )}

            {formStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm text-red-700">
                <i className="ri-error-warning-line w-4 h-4 inline-flex items-center justify-center mr-1" />
                {formMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={formStatus === 'submitting'}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-all outline-none bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed h-11 px-6 text-sm w-full"
            >
              {formStatus === 'submitting' ? (
                <>
                  <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center" />
                  Processing...
                </>
              ) : (
                <>
                  Place Order — {coursePrice}
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </>
              )}
            </button>

            <p className="text-xs text-foreground-500 text-center">
              By placing your order, you agree to our{' '}
              <Link to="/terms-of-service" className="text-primary-500 hover:text-primary-600 underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/refund-policy" className="text-primary-500 hover:text-primary-600 underline">Refund Policy</Link>.
            </p>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}